import { type ReactNode, useCallback, useEffect } from "react";
import { toast } from "sonner";

import {
  type AgendaGapListUploadsControl,
  useGapListUploads,
} from "@src/features/agenda";
import {
  DocumentDropzone,
  type DocumentDropzoneProps,
} from "@src/features/documents";

import {
  CARE_GAP_DOCUMENT_ACCEPTED_FILE_TYPES,
  CARE_GAP_DOCUMENT_ALLOWED_MIME_TYPES,
  CARE_GAP_DOCUMENT_MAX_FILE_SIZE_BYTES,
} from "../careGapDocumentUpload.constants";

export interface PatientCareGapDocumentDropzoneProps
  extends Omit<
    DocumentDropzoneProps,
    | "acceptedFileTypes"
    | "ariaLabel"
    | "children"
    | "hint"
    | "isUploading"
    | "label"
    | "onFilesSelected"
  > {
  patientId: number | null;
  encounterId?: number;
  ariaLabel?: string;
  label?: string;
  hint?: string;
  /**
   * When provided, upload state is owned externally (for example, shared with
   * `AgendaV2`). Otherwise the dropzone manages uploads internally.
   */
  gapListUploads?: AgendaGapListUploadsControl;
  children?:
    | ReactNode
    | ((gapListUploads: AgendaGapListUploadsControl) => ReactNode);
}

function PatientCareGapDocumentDropzoneContent({
  gapListUploads,
  patientId,
  ariaLabel = "Drop care gap file",
  label = "Drop care gap file",
  hint = "We'll pull out each care gap as an agenda item.",
  multiple = false,
  children,
  disabled,
  ...dropzoneProps
}: PatientCareGapDocumentDropzoneProps & {
  gapListUploads: AgendaGapListUploadsControl;
}) {
  const renderedChildren =
    typeof children === "function" ? children(gapListUploads) : children;

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      if (!files.length) return;

      const invalidMime = files.find(
        (file) => !CARE_GAP_DOCUMENT_ALLOWED_MIME_TYPES.has(file.type),
      );
      if (invalidMime) {
        toast.error(`Unsupported file type: ${invalidMime.name}`);
        return;
      }

      const oversized = files.find(
        (file) => file.size > CARE_GAP_DOCUMENT_MAX_FILE_SIZE_BYTES,
      );
      if (oversized) {
        toast.error(`File too large: ${oversized.name} (max 25 MB per file).`);
        return;
      }

      gapListUploads.uploadFiles(files);
    },
    [gapListUploads.uploadFiles],
  );

  return (
    <DocumentDropzone
      {...dropzoneProps}
      acceptedFileTypes={CARE_GAP_DOCUMENT_ACCEPTED_FILE_TYPES}
      ariaLabel={ariaLabel}
      disabled={disabled || patientId == null}
      hint={hint}
      isUploading={gapListUploads.isUploading}
      label={label}
      multiple={multiple}
      onFilesSelected={handleFilesSelected}
    >
      {renderedChildren}
    </DocumentDropzone>
  );
}

function PatientCareGapDocumentDropzoneWithInternalUploads(
  props: Omit<PatientCareGapDocumentDropzoneProps, "gapListUploads">,
) {
  const { patientId, encounterId } = props;
  const gapListUploads = useGapListUploads({
    patientId: patientId ?? undefined,
    encounterId,
  });

  useEffect(() => {
    gapListUploads.resetUploads();
  }, [gapListUploads.resetUploads, patientId]);

  return (
    <PatientCareGapDocumentDropzoneContent
      {...props}
      gapListUploads={gapListUploads}
    />
  );
}

export function PatientCareGapDocumentDropzone({
  gapListUploads,
  ...props
}: PatientCareGapDocumentDropzoneProps) {
  if (gapListUploads != null) {
    return (
      <PatientCareGapDocumentDropzoneContent
        {...props}
        gapListUploads={gapListUploads}
      />
    );
  }

  return <PatientCareGapDocumentDropzoneWithInternalUploads {...props} />;
}
